import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import "../../../styles/dashboard-tokens.css";
import { AutumnInfo } from "./AutumnInfo";
// `hero.jpg` lives at `src/assets/auth/hero.jpg`. Webpack/CRA resolves
// the import to a URL string at build time. If the file is missing the
// build will fail with a clear "Module not found" error pointing at
// this line — drop the photo into that folder and the import resolves.
import heroImage from "../../../assets/auth/hero.jpg";

/**
 * Slot context exposed by `AuthSplitLayout`. Children rendered inside the
 * shell (e.g. the Onboarding sidebar/view components) can override the
 * default left/right column contents without touching the shell chrome
 * (hero photo, cream card, cutout). When `setLeftSlot` is invoked with
 * a non-null node, the default `<AutumnInfo invert />` block is replaced.
 * Likewise `setRightSlot` overrides the layout's direct `children` slot.
 * This is the mechanism used to plug Onboarding screens into the same
 * visual shell as the Login/Register forms — so the user sees one
 * continuous surface across auth + onboarding instead of a hard nav
 * between two unrelated pages.
 */
interface AuthShellSlots {
  setLeftSlot: (node: ReactNode | null) => void;
  setRightSlot: (node: ReactNode | null) => void;
  /**
   * Canvas overlaying the cream form/step card, clipped by the SAME
   * cutout mask as the card surface. Fire scoped celebrations here
   * (e.g. the onboarding Congrats confetti) so particles stay inside
   * the card — including the notch bite — instead of covering the
   * whole viewport. Null until the shell mounts.
   */
  celebrationCanvas: HTMLCanvasElement | null;
}

const AuthShellSlotsContext = createContext<AuthShellSlots | null>(null);

export function useAuthShellSlots(): AuthShellSlots | null {
  return useContext(AuthShellSlotsContext);
}

/**
 * SVG cutout path supplied by Viktor — a concave "bite" out of the
 * form card's left edge. Designed at 49×87.
 *   - Encoded as a URL-safe data URI so it can be used as a CSS mask.
 *   - The cream-soft form card sits beside the photo on the right;
 *     the mask carves the notch out of the card's left edge so the
 *     photo (which fills the whole page background) shows through.
 */
const CUTOUT_SVG =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='49' height='87' viewBox='0 0 49 87'>` +
      `<path d='M0 0C0 6.95363 3.90697 22.332 15.6105 27.7499C23.0564 31.1968 30.3832 32.9223 36.185 34.2701C39.0784 34.9422 41.6082 35.5237 43.5688 36.1798C44.5496 36.5081 45.404 36.8592 46.0996 37.2595C46.7861 37.6546 47.3648 38.1231 47.7523 38.7211C48.7313 40.2318 49.0408 41.9331 48.9958 43.5C49.0408 45.0669 48.7313 46.7682 47.7523 48.2789C47.3648 48.8769 46.7861 49.3454 46.0996 49.7405C45.404 50.1408 44.5496 50.4919 43.5688 50.8202C41.6082 51.4763 39.0784 52.0578 36.185 52.7299C30.3832 54.0777 23.0564 55.8032 15.6105 59.2501C3.90714 64.6679 0.000112209 80.0462 0 87V43.5V0Z' fill='black'/>` +
      `</svg>`
  );

/**
 * Shared mask declarations for the card cutout — full rect MINUS the
 * cutout SVG. Used by BOTH the cream card surface and the transparent
 * celebration-canvas overlay so confetti is clipped by the exact same
 * shape (card bounds + notch bite). mask-size / mask-position live in
 * the `.auth-shell-cutout` <style> block so they stay responsive and
 * animatable.
 */
const CUTOUT_MASK_STYLE: React.CSSProperties = {
  WebkitMaskImage: `linear-gradient(#000, #000), url("${CUTOUT_SVG}")`,
  maskImage: `linear-gradient(#000, #000), url("${CUTOUT_SVG}")`,
  WebkitMaskRepeat: "no-repeat, no-repeat",
  maskRepeat: "no-repeat, no-repeat",
  WebkitMaskComposite: "source-out",
  maskComposite: "subtract",
};

interface AuthSplitLayoutProps {
  children: React.ReactNode;
  /**
   * Selects the hero copy shown in the default left column (when no
   * `leftSlot` override is pushed). `/register` passes `"marketing"` for
   * campaign signups so Omar's marketing copy renders; everything else
   * uses the standard `"default"` pitch.
   */
  leftVariant?: "default" | "marketing";
  /**
   * Render the onboarding column proportions (25/75 + cutout at the onboarding
   * offset) from the very first paint. Without it the shell starts at the
   * auth 50/50 split and only widens once the onboarding slots populate via
   * effects — which reads as an ugly width slide on reload. Onboarding passes
   * this so the correct layout is shown immediately. Auth pages omit it.
   */
  onboardingLayout?: boolean;
}

/**
 * Auth page shell used by /login, /register, AND /onboarding.
 *
 * Structure (desktop, lg+):
 *   ┌────────────────── padding 24 ──────────────────┐
 *   │ full-bleed hero photo + dark gradient wash     │
 *   │                                                │
 *   │ ┌── left 3/12 ──┐ gap ┌── right 9/12 ────────┐ │
 *   │ │  ┌          ┐  │ 24 │ ╮  cream-soft card   │ │
 *   │ │  AutumnInfo   │      │ ◀──cutout           │ │
 *   │ │  (centred)    │      │ ╯                   │ │
 *   │ │  └          ┘  │      │  fields + submit   │ │
 *   │ │ corners on    │      │                     │ │
 *   │ │  its corners  │      │                     │ │
 *   │ └───────────────┘      └─────────────────────┘ │
 *   └────────────────────────────────────────────────┘
 *
 * - The hero photo paints the entire viewport (outer wrapper bg).
 * - Left column is TRANSPARENT — photo shows through. 4 corner
 *   brackets sit on its own 4 corners (not the viewport's).
 * - Right column is a cream-soft card with a 12px radius and a
 *   concave SVG cutout on its left edge — the photo shows through
 *   the notch.
 * - Padding outside the columns: 24px desktop, 16px mobile.
 * - Gap between columns: 24px desktop, 16px mobile.
 * - Column split: 3/12 left, 9/12 right (was 50/50 — narrowed the brand
 *   column so the form/step content has more breathing room, matching
 *   the design mockup).
 *
 * Mobile (< lg):
 *   - Columns stack vertically (photo behind everything).
 *   - The cutout mask is suppressed (notch points at nothing when
 *     the photo is above instead of beside the card).
 */
export function AuthSplitLayout({
  children,
  leftVariant = "default",
  onboardingLayout = false,
}: AuthSplitLayoutProps) {
  // Slot overrides — see AuthShellSlots comment. Stay null for the
  // standalone /login + /register usage; populated by Onboarding screens.
  const [leftSlot, setLeftSlot] = useState<ReactNode | null>(null);
  const [rightSlot, setRightSlot] = useState<ReactNode | null>(null);
  // Celebration canvas — held in state (not a ref) so the context value
  // updates once when the canvas mounts and consumers re-render with it.
  const [celebrationCanvas, setCelebrationCanvas] =
    useState<HTMLCanvasElement | null>(null);

  // Memoise the context value so consumers don't see a new `slots` object
  // every render — without this the OnboardingSidebar / OnboardingView
  // effects observe a "changed" dep on every render → setSlot → re-render
  // → new value → infinite loop ("Maximum update depth exceeded").
  // useState setters are stable across renders, so the value only changes
  // when the celebration canvas mounts/unmounts (a single extra cycle).
  const slotsValue = useMemo(
    () => ({ setLeftSlot, setRightSlot, celebrationCanvas }),
    [celebrationCanvas]
  );

  const isOnboardingShell = onboardingLayout || !!leftSlot || !!rightSlot;

  return (
    <AuthShellSlotsContext.Provider value={slotsValue}>
    {/* Mask-position rules for the left-edge cutout on the form/step
        card. Driving via CSS (not inline style) lets us use media queries
        AND animate `mask-position` smoothly when the `.is-onboarding`
        class toggles on/off — login/register → onboarding pulls the
        bite upward instead of snapping.
        Y-offsets:
          mobile (< 1170px):    60px from top (any state)
          desktop (≥ 1170px):
            auth (default):    200px from top
            onboarding:        180px from top (20px raised — sits
                               just below the progress bar). */}
    <style>{`
      .auth-shell-cutout {
        /* Mobile: smaller cutout — 20px wide, height preserves the
           SVG's 49:87 aspect ratio (20 × 87/49 ≈ 35.5px). Y-offset
           30px from the top of the card. */
        -webkit-mask-size: 100% 100%, 20px 35.5px;
        mask-size: 100% 100%, 20px 35.5px;
        -webkit-mask-position: center, left 40px;
        mask-position: center, left 40px;
        transition: -webkit-mask-position 380ms cubic-bezier(0.4, 0, 0.2, 1),
                    mask-position 380ms cubic-bezier(0.4, 0, 0.2, 1);
      }
      @media (max-width: 1169px) {
        .auth-shell-cutout.is-onboarding {
          /* Onboarding mobile: taller cutout (132px) for the step card, 45px
             from the top. Scoped to < 1170px so the two-class selector doesn't
             out-specify the desktop rules below. */
          -webkit-mask-size: 100% 100%, 20px 132px;
          mask-size: 100% 100%, 20px 132px;
          -webkit-mask-position: center, left 45px;
          mask-position: center, left 45px;
        }
      }
      @media (min-width: 1170px) {
        .auth-shell-cutout {
          /* Desktop: full-size 49 × 87 cutout. */
          -webkit-mask-size: 100% 100%, 49px 87px;
          mask-size: 100% 100%, 49px 87px;
          -webkit-mask-position: center, left 200px;
          mask-position: center, left 200px;
        }
        .auth-shell-cutout.is-onboarding {
          /* Onboarding desktop: raised to sit just below the progress
             bar — 110px from the top. */
          -webkit-mask-position: center, left 110px;
          mask-position: center, left 110px;
        }
      }
    `}</style>
    <div
      data-design="refined-teal"
      className={`auth-layout h-[100dvh] overflow-y-auto overscroll-contain w-full relative px-4 pt-[calc(1rem+var(--sat))] pb-[calc(1rem+var(--sab))] dlg:p-6 flex flex-col ${
        isOnboardingShell ? "dlg:overflow-hidden" : ""
      }`}
    >
      {/* Fixed hero background — stays put while the form scrolls on mobile.
          Uses `position: fixed` rather than `background-attachment: fixed`
          (unreliable on iOS Safari). The form-card cutout still reveals it. */}
      <div
        aria-hidden
        className="fixed inset-0 z-0 pointer-events-none"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.50) 0%, rgba(0,0,0,0.15) 100%), url('${heroImage}')`,
          backgroundSize: "cover",
          // Anchor to bottom-center so the hero's foreground stays visible.
          backgroundPosition: "center bottom",
          backgroundRepeat: "no-repeat",
        }}
      />
      {/* Inner flex row claims the remaining height via `flex-1`. The
          outer wrapper is `min-h-screen flex flex-col`, so this row
          automatically expands to fill the space left between the
          24px top + bottom padding — no `calc(100vh - …)` hacks.
          Onboarding also fills on MOBILE (`flex-1 min-h-0` below dlg):
          the step components size themselves with `flex-1 min-h-0`
          chains that need a definite ancestor height to resolve —
          without it the step card neither reaches the viewport bottom
          nor gives its internal lists (e.g. Competitors) real heights.
          Auth pages keep the content-driven column so the whole page
          scrolls on mobile as before. */}
      <div
        // Auth pages grow and scroll when taller than the viewport; onboarding stays viewport-locked.
        className={`relative z-10 w-full flex flex-col dlg:flex-row gap-4 dlg:gap-6 ${
          isOnboardingShell
            ? "flex-1 min-h-0 dlg:flex-1 dlg:min-h-0"
            : "dlg:flex-[1_0_auto]"
        }`}
      >
        {/* Left column — transparent (photo shows through) with 4
            decorative corner brackets pinned to its own corners.
            AutumnInfo centred both axes inside the column.
            Width:
              - /login, /register (no slot active): 50% — keeps the
                marketing hero block readable at the original size.
              - /onboarding (slot active): 25% — narrows the brand
                column so the step content can take 75% of the row.
            The width transition is animated so /register → /onboarding
            slides smoothly instead of snapping. */}
        <div
          className={`relative flex items-center justify-center px-4 py-3 max-h-[45dvh] overflow-hidden dlg:max-h-none dlg:overflow-visible ${
            isOnboardingShell ? "dlg:w-1/4 dlg:px-4 dlg:py-10 xl:px-6" : "dlg:w-1/2 dlg:px-12 dlg:py-10 xl:px-16"
          }`}
          style={{ transition: "width 380ms var(--d-ease, cubic-bezier(0.4, 0, 0.2, 1)), padding 380ms var(--d-ease, cubic-bezier(0.4, 0, 0.2, 1))" }}
        >
          {/* Corner brackets — anchored to the LEFT column's 4
              corners. Inset is 0 because the outer wrapper already
              gives us 24px of breathing room on every side; another
              12px on the brackets would push them awkwardly inward. */}
          <CornerBracket className="absolute top-0 left-0" />
          <CornerBracket className="absolute top-0 right-0 rotate-90" />
          <CornerBracket className="absolute bottom-0 left-0 -rotate-90" />
          <CornerBracket className="absolute bottom-0 right-0 rotate-180" />

          {/* Same AutumnInfo component renders in BOTH branches. When the
              slot is null (/login, /register) the default props apply —
              hero copy + standard 16px body. When OnboardingSidebar pushes
              an AutumnInfo element with onboarding props into the slot,
              React reconciles it as the SAME component and tweens between
              the two prop sets via the CSS transitions on h1/p, so
              /register → /onboarding doesn't pop. */}
          {leftSlot ?? <AutumnInfo invert variant={leftVariant} />}
        </div>

        {/* Right column — cream-soft form card with cutout on the
            left edge. The card surface is `absolute inset-0` so it
            fills the column, then a relative inner div carries the
            form on top. Height stretches to the flex row's height
            (no min-h calc needed).
            Width:
              - /login, /register: 50% (matches the original auth split
                so the form sits where users remember it).
              - /onboarding: 75% — gives the wider step Cards room to
                breathe and matches the design mockup's 3:9 layout.
            Width transition animated for the /register → /onboarding
            slide. When a slot is active we also drop the inner padding
            + 420px form cap so the slot owns the full card. */}
        <div
          className={`relative mx-auto dlg:mx-0 w-full flex flex-col dlg:flex-none ${
            rightSlot || onboardingLayout
              ? "dlg:w-3/4 max-w-none flex-1 min-h-0"
              : `${leftSlot ? "dlg:w-3/4" : "dlg:w-1/2"} items-center justify-center px-5 py-10 sm:px-8 sm:py-12 dlg:px-16 dlg:py-20 max-w-[560px] dlg:max-w-none`
          }`}
          style={{ transition: "width 380ms var(--d-ease, cubic-bezier(0.4, 0, 0.2, 1))" }}
        >
          {/* Card surface — cream-soft + concave cutout on the LEFT
              edge. Mask = full rect MINUS the cutout SVG, so the
              hero photo (page background) shows through the bite.
              Used on BOTH mobile and desktop now — on mobile when
              the right column sits below the hero column, the bite
              still reads against the page photo bleeding through.
              Y-offset of the cutout is controlled via a CSS variable
              (`--cutout-y`) so the transition between auth (default
              position) and onboarding (raised) is animatable. The
              actual values live in the `<style>` block above:
                desktop auth:        200px from top
                desktop onboarding:  180px from top  (20px higher)
                mobile (any state):   60px from top
              The `is-onboarding` class swaps in the onboarding offsets;
              `transition: mask-position …` tweens the slide. */}
          <div
            className={`auth-shell-cutout absolute inset-0 pointer-events-none ${
              isOnboardingShell ? "is-onboarding" : ""
            }`}
            style={{
              background: "var(--d-cream-soft)",
              borderRadius: 12,
              // mask-size / mask-position live in the <style> block at the
              // top so they can vary per breakpoint and animate via
              // `transition: mask-position` — inline values would win the
              // specificity battle and block the CSS rules.
              ...CUTOUT_MASK_STYLE,
              zIndex: 0,
            }}
            aria-hidden
          />

          {/* Celebration overlay — same cutout mask as the card surface,
              but transparent and ABOVE the content (z-2 vs content z-1),
              so confetti fired into the canvas flies over the step content
              yet never escapes the card bounds or the notch bite. */}
          <div
            className={`auth-shell-cutout absolute inset-0 pointer-events-none ${
              isOnboardingShell ? "is-onboarding" : ""
            }`}
            style={{ ...CUTOUT_MASK_STYLE, zIndex: 2 }}
            aria-hidden
          >
            <canvas
              ref={setCelebrationCanvas}
              className="absolute inset-0 h-full w-full"
            />
          </div>

          {/* Form column — relative so it stacks above the card
              surface. Login/Register use a narrow centred form
              (max-w-[420px], gap-5 stack). Onboarding takes the slot
              path and the slot content fills the whole column — the
              step Cards are intentionally wider than the auth form
              and shouldn't be clamped down. The hidden mirror keeps
              `children` (the Step tree owning OnboardingSidebar /
              OnboardingView) mounted so their slot-setting effects
              don't tear down on every state change. */}
          <div
            className={`relative z-[1] flex flex-col ${
              rightSlot || onboardingLayout
                ? "w-full flex-1 min-h-0"
                : "w-full items-stretch gap-5 dlg:max-w-[420px]"
            }`}
          >
            {rightSlot ?? children}
            {rightSlot ? (
              <div className="hidden" aria-hidden>
                {children}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
    </AuthShellSlotsContext.Provider>
  );
}

/**
 * Decorative `└` corner bracket — used 4× around the left column.
 * 15×15px on mobile, 22×22px on lg+ (Viktor's "30% smaller on mobile"
 * spec — 22 × 0.70 ≈ 15).
 */
function CornerBracket({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 22 22"
      fill="none"
      className={`w-[15px] h-[15px] dlg:w-[22px] dlg:h-[22px] ${className ?? ""}`}
      aria-hidden
    >
      {/* Corner curve radius matches the form / step card's
          `--d-r-xl` (10px) so the bracket's inner curve reads as a
          continuation of the card's rounded corner instead of a
          tighter ~5px arc that visually fights it. SVG viewBox is
          22 — radius 10 means the straight legs end at x/y=11 and
          the arc spans (22,1) → (1,11) → (1,22). */}
      <path
        d="M22 1H11C5.47715 1 1 5.47715 1 11V22"
        stroke="rgba(255,255,255,0.65)"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  );
}
